import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import { useCurrentCase } from "./useCurrentCase";

export interface LegalOsKeyword {
  id: string;
  keyword: string;
  primary_intent: string;
  priority_score: number;
  fixed_status: string;
  recommended_page_type: string | null;
  reason: string | null;
  target_page_id: string | null;
}

export interface LegalOsPage {
  id: string;
  url_path: string;
  page_type: string;
  target_keyword_id: string | null;
  title: string;
  h1: string | null;
  status: string;
  effective_status?: string;
  public_url?: string | null;
  first_view_image_url?: string | null;
  primary_value_pattern: string | null;
  required_assets: string[];
  missing_assets: string[];
}

export interface LegalOsRecommendation {
  id: string;
  type: "SEO" | "CVR" | "CONTENT" | "ASSET";
  title: string;
  description: string;
  impact_score: number;
  confidence_score: number;
  effort_score: number;
  risk_score: number;
  status: string;
}

export interface LegalOsPersona {
  id: string;
  name: string;
  role: string;
  situation: string | null;
  pain_points: string[];
  conversion_trigger: string | null;
}

export interface LegalOsBundle {
  profile: Record<string, unknown> | null;
  personas: LegalOsPersona[];
  keywords: LegalOsKeyword[];
  pages: LegalOsPage[];
  recommendations: LegalOsRecommendation[];
  practice_areas: Array<Record<string, unknown>>;
  service_areas: Array<Record<string, unknown>>;
}

export function useLegalOs() {
  const { caseRow, loading: caseLoading } = useCurrentCase();
  const [os, setOs] = useState<LegalOsBundle | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!caseRow) return null;
    setLoading(true);
    try {
      const res = await api.get<{ os: LegalOsBundle }>(`/api/cases/${caseRow.id}/os`);
      setOs(res.os);
      return res.os;
    } finally {
      setLoading(false);
    }
  }, [caseRow?.id]);

  useEffect(() => {
    if (caseLoading) return;
    if (!caseRow) {
      setLoading(false);
      return;
    }
    void reload();
  }, [caseLoading, caseRow?.id, reload]);

  return { caseRow, os, setOs, reload, loading: loading || caseLoading };
}
